import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import { cloneDeep } from 'lodash';

const DataContext = createContext(null)

export const useDataContext = (key) => {
    const context = useContext(DataContext)
    if (!context) {
        throw new Error(`useDataContext must be used within a DataProvider`)
    }

    const { data, setData, history, setHistory, future, setFuture, surveyId } = context
    const maxHistorySize = 50

    // Include surveyId in the storage key for surveyData and userSurveyData
    const shouldIncludeSurveyId = key === 'surveyData' || key === 'userSurveyData'
    const storageKey = shouldIncludeSurveyId && surveyId ? `${key}_${surveyId}` : key

    // Keys that should not be tracked in history
    const excludedKeysFromHistory = ['userData', 'categoryData', 'contactsData', 'supportRolesData', 'branchOptionErrors']

    const memoizedValue = useMemo(() => cloneDeep(data[key]), [data[key], key])

    return {
        value: memoizedValue,
        setValue: (value, updateHistory = true) => {
            setData((prevData) => {
                // Update history and clear future only if the key is not in the excluded list
                if (updateHistory && !excludedKeysFromHistory.includes(key)) {
                    setHistory([...history, prevData].slice(-maxHistorySize))
                    setFuture([])
                }
                // Save to local storage only for surveyData and userSurveyData
                if (shouldIncludeSurveyId) {
                    sessionStorage.setItem(storageKey, JSON.stringify(value))
                }
                return { ...prevData, [key]: value }
            });
        },
        undo: () => {
            if (history.length === 0) return;
            const lastState = history[history.length - 1];
            setHistory(history.slice(0, history.length - 1));
            setFuture([data, ...future]);

            // Preserve the current values of the excluded keys
            const preservedData = {};
            excludedKeysFromHistory.forEach(excludedKey => {
                preservedData[excludedKey] = data[excludedKey];
            });

            setData({ ...lastState, ...preservedData });
        },
        redo: () => {
            if (future.length === 0) return;
            const nextState = future[0];
            setFuture(future.slice(1));
            setHistory([...history, data]);

            // Preserve the current values of the excluded keys
            const preservedData = {};
            excludedKeysFromHistory.forEach(excludedKey => {
                preservedData[excludedKey] = data[excludedKey];
            });

            setData({ ...nextState, ...preservedData });
        },
        history,
        future,
    }
}

export const DataProvider = ({ children, surveyId }) => {
    // Load initial data from local storage
    const getInitialData = () => {
        const initialState = {};
        // Load surveyData and userSurveyData with surveyId
        if (surveyId) {
            const surveyDataKey = `surveyData_${surveyId}`;
            const userSurveyDataKey = `userSurveyData_${surveyId}`;
            const storedSurveyData = sessionStorage.getItem(surveyDataKey);
            const storedUserSurveyData = sessionStorage.getItem(userSurveyDataKey);
            initialState['surveyData'] = storedSurveyData ? JSON.parse(storedSurveyData) : null;
            initialState['userSurveyData'] = storedUserSurveyData ? JSON.parse(storedUserSurveyData) : null;
        }
        return initialState;
    }

    const [data, setData] = useState(getInitialData())
    const [history, setHistory] = useState([])
    const [future, setFuture] = useState([])

    return (
        <DataContext.Provider value={{ data, setData, history, setHistory, future, setFuture, surveyId }}>{children}</DataContext.Provider>
    )
}

export const CollapsedViewContext = React.createContext(null)

export const CollapsedViewProvider = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = React.useState(false);

    return (
        <CollapsedViewContext.Provider value={{ isCollapsed, setIsCollapsed }}>
            {children}
        </CollapsedViewContext.Provider>
    );
};
